import React, { useState, useEffect } from "react";
import { MainBodyStyle } from "../../styles/MainBodyStyles";
import TopBar from "../../components/TopBar/TopBar";
import Footer from "../../components/footer/Footer";
import { DownloadSponsorReportPageStyle } from "./Configuration.style";
import { useLocation } from "react-router-dom";
import { downloadReportvalidationSchema } from "../../validations/DownloadReportValidation";
import { TextErrorStyle3 } from "../../styles/ErrorStyles";
import { HeadingStyle, ThreeFieldRow } from "../../styles/HeadingStyles";
import { downloadSponsorAnalyticReport } from "../../api/SponsorshipApi";

const DownloadSponsorReports = () => {
  const location = useLocation();
  const { sponsorName, sponsorId } = location.state || {};
  
  // Debug log to verify sponsorId from location state
  useEffect(() => {
    console.log("DownloadSponsorReports - Location state:", location.state);
    console.log("DownloadSponsorReports - Sponsor ID:", sponsorId);
  }, [location.state, sponsorId]);
  
  const [email, setEmail] = useState("");
  const [usePreset, setUsePreset] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [preset, setPreset] = useState("");
  const [errors, setErrors] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [instantmsg, setInstantmsg] = useState(true);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("12:00"); // Default to noon
  const [recurrence, setRecurrence] = useState("");

  // Clear manual dates when switching back to preset
  useEffect(() => {
    if (usePreset) {
      setStartDate("");
      setEndDate("");
    }
  }, [usePreset]);

  const handlePresetChange = async (e) => {
    const selectedPreset = e.target.value;
    setPreset(selectedPreset);

    const today = new Date();
    let start, end;

    switch (selectedPreset) {
      case "Today":
        start = end = today;
        break;
      case "Yesterday":
        start = end = new Date(today);
        start.setDate(today.getDate() - 1);
        end.setDate(today.getDate() - 1);
        break;
      case "Last 7 Days":
        end = today;
        start = new Date(today);
        start.setDate(today.getDate() - 6);
        break;
      case "Last 30 Days":
        end = today;
        start = new Date(today);
        start.setDate(today.getDate() - 29);
        break;
      case "Last 90 Days":
        end = today;
        start = new Date(today);
        start.setDate(today.getDate() - 89);
        break;
      case "Last 365 Days":
        end = today;
        start = new Date(today);
        start.setDate(today.getDate() - 364);
        break;
      default:
        start = end = "";
    }

    const format = (date) => (date ? date.toISOString().slice(0, 10) : "");

    setStartDate(format(start));
    setEndDate(format(end));
  };

  // Replace handleValidate parameters to include recurrence
  const handleValidate = async () => {
    try {
      await downloadReportvalidationSchema.validate(
        { email, startDate, endDate, schedule, scheduleDate, scheduleTime, recurrence },
        { abortEarly: false }
      );
      setErrors({});
      return true;
    } catch (err) {
      const newErrors = {};
      if (err.inner) {
        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
      }
      console.log("Validation error:", newErrors);
      setErrors(newErrors);
      return false;
    }
  };

  // Replace handleDownload stub with actual API integration
  const handleDownload = async () => {
    if (!(await handleValidate())) return;
    try {
      const accessToken = localStorage.getItem("accessToken");
      const blob = await downloadSponsorAnalyticReport({
        email,
        isScheduled: schedule,
        startDate,
        endDate,
        recurrence,
        scheduleDate,
        scheduleTime,
        sponsorId
      }, accessToken);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SponsorAnalyticsReport_${sponsorId}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download Sponsor Analytics Report:", error);
    }
  };

  return (
    <DownloadSponsorReportPageStyle>
      <TopBar authorized={true} />
      <MainBodyStyle>
        <div className="configurationCard">
          <HeadingStyle>Download Sponsor Analytics Report</HeadingStyle>
          <div style={{ marginTop: "50px" }} />
          <div className="headingStyle">
            <p className="heading_text">Sponsor Name:</p>
            <p>{sponsorName}</p>
          </div>
          <div className="headingStyle">
            <p className="heading_text">Email *:</p>

            <div className="input-div">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                id="email"
                maxLength={30}
                placeholder="Enter Email"
                className="form-control mt-1 input-field"
              />
              {errors.email && (
                <TextErrorStyle3>{errors.email}</TextErrorStyle3>
              )}
            </div>
          </div>
          {/* Preset Toggle */}
          <div className="headingStyle">
            <p className="heading_text">Preset</p>
            <div className="radio-div2">
              <div className="form-check2">
                <input
                  type="radio"
                  name="presetMode"
                  className="form-check-input"
                  checked={usePreset}
                  onChange={() => setUsePreset(true)}
                />
                <label className="form-check-label">Yes</label>
              </div>
              <div className="form-check2">
                <input
                  type="radio"
                  name="presetMode"
                  className="form-check-input"
                  checked={!usePreset}
                  onChange={() => setUsePreset(false)}
                />
                <label className="form-check-label">No</label>
              </div>
            </div>
          </div>

          {/* Preset label and dropdown */}
          {usePreset && (
            <div className="headingStyle">
              <p className="heading_text">Preset:</p>
              <div className="input-div">
                <select
                  className="form-control mt-1 input-field"
                  value={preset || ""}
                  onChange={handlePresetChange}
                >
                  <option value="" disabled>
                    Select Preset
                  </option>
                  {[
                    "Today",
                    "Yesterday",
                    "Last 7 Days",
                    "Last 30 Days",
                    "Last 90 Days",
                    "Last 365 Days",
                  ].map((item, index) => (
                    <option key={index} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Manual date pickers */}
          {!usePreset && (
            <>
              <div className="headingStyle2">
                <div>
                  <p className="heading_text">Start Date *:</p>
                </div>
                <div className="headingStyle3">
                  <p className="heading_text">End Date *:</p>
                </div>
              </div>
              <div className="headingStyle2">
                <div className="input-div">
                  <input
                    type="date"
                    value={startDate}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                    }}
                    className="form-control mt-1 input-field"
                    style={{ minWidth: "150px" }}
                    placeholder="Start Date"
                  />
                  {errors.startDate && (
                    <TextErrorStyle3>{errors.startDate}</TextErrorStyle3>
                  )}
                </div>
                <div className="input-div">
                  <input
                    type="date"
                    value={endDate}
                    max={new Date().toISOString().split("T")[0]}
                    className="form-control mt-1 input-field"
                    style={{ minWidth: "150px" }}
                    placeholder="End Date"
                    onChange={(e) => {
                      setEndDate(e.target.value);
                    }}
                  />
                  {errors.endDate && (
                    <TextErrorStyle3>{errors.endDate}</TextErrorStyle3>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="headingStyle">
            <p className="heading_text">Schedule Report:</p>
            <div className="radio-div2">
              <div className="form-check2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="flexRadioSchedule"
                  id="scheduleReport"
                  checked={schedule}
                  onChange={() => {
                    setSchedule(true);
                    setInstantmsg(false);
                  }}
                />
                <label className="form-check-label" htmlFor="flexRadioSchedule">
                  Yes
                </label>
              </div>
              <div className="form-check2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="flexRadioInstant"
                  id="instant"
                  checked={instantmsg}
                  onChange={() => {
                    setInstantmsg(true);
                    setSchedule(false);
                  }}
                />
                <label className="form-check-label" htmlFor="flexRadioInstant">
                  No
                </label>
              </div>
            </div>
          </div>
          {schedule && (
            <ThreeFieldRow>
              <div className="field">
                <p className="heading_text">Schedule Date *:</p>
                <input
                  type="date"
                  value={scheduleDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="form-control mt-1 input-field"
                />
                {errors.scheduleDate && <TextErrorStyle3>{errors.scheduleDate}</TextErrorStyle3>}
              </div>
              <div className="field">
                <p className="heading_text">Schedule Time *:</p>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="form-control mt-1 input-field"
                />
                {errors.scheduleTime && <TextErrorStyle3>{errors.scheduleTime}</TextErrorStyle3>}
              </div>
              <div className="field">
                <p className="heading_text">Recurrence *:</p>
                <select
                  className="form-control mt-1 input-field"
                  value={recurrence}
                  onChange={(e) => setRecurrence(e.target.value)}
                >
                  <option value="Once">Once</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
                {errors.recurrence && <TextErrorStyle3>{errors.recurrence}</TextErrorStyle3>}
              </div>
            </ThreeFieldRow>
          )}

          <div className="btn-div">
            <button
              className="btn form-button"
              onClick={() => {
                handleDownload();
              }}
            >
              {schedule ? "Schedule" : "Download"}
            </button>
          </div>
        </div>
      </MainBodyStyle>
      <Footer style="footer" />
    </DownloadSponsorReportPageStyle>
  );
};

export default DownloadSponsorReports;
